import React, { useState, useEffect, useRef } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import Input from '../common/Input';
import Button from '../common/Button';
import { toast } from 'react-hot-toast';
import { ShareService } from '../../services/share.service';
import { TeamService } from '../../services/team.service';
import { useAppSelector } from '../../hooks/useRedux';
import { Role, Team } from '../../types/team.types';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { RolesService } from '../../services/roles.service';
import { hasPermission } from '../../utils/permissionUtils';

interface ShareVaultModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => Promise<void> | void;
    vaultId: string;
    vaultName: string;
}

interface User {
    id: string;
    email: string;
}

const ShareVaultModal: React.FC<ShareVaultModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    vaultId,
    vaultName
}) => {
    const { team_id, role_id, permissions} = useAppSelector((state) => state.auth);
    const [shareType, setShareType] = useState<'individual' | 'team'>('individual');
    const [email, setEmail] = useState('');
    const [permissionLevel, setPermissionLevel] = useState<'read' | 'write' | 'manage'>('read');
    const [expiresIn, setExpiresIn] = useState('never');
    const [isLoading, setIsLoading] = useState(false);
    const [organizations, setOrganizations] = useState<Team[]>([]);
    const [selectedTeamId, setSelectedTeamId] = useState<string>('');
    const { activeWorkspace } = useWorkspace();

    // New state variables for user search/select
    const [availableUsers, setAvailableUsers] = useState<User[]>([]);
    const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
    const [searchText, setSearchText] = useState('');
    const [selectedUserId, setSelectedUserId] = useState<string>('');
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);

    const canShareWithTeam = hasPermission(permissions, 'vault', 'vault_share_teams');

    const dropdownRef = useRef<HTMLDivElement>(null);

    // Add new state variables
    const [showInviteForm, setShowInviteForm] = useState(false);
    const [inviteRoleId, setInviteRoleId] = useState<number>(0);
    const [isInviting, setIsInviting] = useState(false);
    const [inviteError, setInviteError] = useState('');
    const [roles, setRoles] = useState<Role[]>([]);

    // Add click outside listener to close dropdown
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        }
        
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);
    

    
    // Fetch organizations when modal opens for admins
    useEffect(() => {
        if (isOpen && canShareWithTeam && shareType === 'team') {
            fetchTeams();
        }
    }, [isOpen, canShareWithTeam, shareType]);

    // Fetch active team members when modal opens
    useEffect(() => {
        if (isOpen && shareType === 'individual') {
            fetchActiveTeamMembers();
        }
    }, [isOpen, shareType]);

    // Update filtered users whenever search text changes
    useEffect(() => {
        if (!searchText.trim()) {
            setFilteredUsers(availableUsers);
            setIsDropdownOpen(false);
        } else {
            const filtered = availableUsers.filter(user => 
                user.email.toLowerCase().includes(searchText.toLowerCase())
            );
            setFilteredUsers(filtered);
            setIsDropdownOpen(true);
        }
    }, [searchText, availableUsers]);

    // Add useEffect to fetch roles when needed
    useEffect(() => {
        if (showInviteForm) {
            fetchRoles();
        }
    }, [showInviteForm]);

    const fetchActiveTeamMembers = async () => {
        setIsLoadingUsers(true);
        try {
            const response = await TeamService.getActiveTeamMembers(team_id || undefined);
            const users = response.activeMembers.map((member: any) => ({
                id: member.user_id,
                email: member.email
            }));
            console.log('users', users);
            setAvailableUsers(users);
            setFilteredUsers(users);
        } catch (error) {
            console.error('Error fetching active team members:', error);
            toast.error('Failed to load team members');
        } finally {
            setIsLoadingUsers(false);
        }
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchText(value);
        setEmail(value);
        if (value === '') {
            setSelectedUserId('');
            setSelectedUser(null);
            setIsDropdownOpen(false);
        }
    };

    const handleUserSelect = (user: User) => {
        setIsDropdownOpen(false);
        setSelectedUserId(user.id);
        setSelectedUser(user);
        // Set these last to avoid triggering the search effect
        setEmail(user.email);
        setSearchText(user.email);
    };

    const handleFocus = () => {
        setIsDropdownOpen(true);
    };
    
    const fetchTeams = async () => {
        try {
            const response = await TeamService.getUserTeams();

            // Filter teams (teams with no parent)
            const teams = response.teams?.filter(team => 
                team.team_type === "department" || !team.parent_team_id
            ) || [];
            setOrganizations(teams as Team[]);
            
            // Set default team if available
            if (teams.length > 0) {
                setSelectedTeamId(teams[0].id);
            }
        } catch (error) {
            console.error('Error fetching organizations:', error);
            toast.error('Failed to load organizations');
        }
    };

    const handleShare = async (e: React.FormEvent) => {
        e.preventDefault();

        if (shareType === 'individual' && !email) {
            toast.error('Please enter an email address');
            return;
        }
        
        if (shareType === 'team' && canShareWithTeam && !selectedTeamId) {
            toast.error('Please select an organization');
            return;
        }

        setIsLoading(true);

        try {
            const expiresAt = expiresIn === 'never'
                ? undefined
                : new Date(Date.now() + parseInt(expiresIn) * 24 * 60 * 60 * 1000);

            // Create itemIds with just the vault
            const itemIds = [{
                id: vaultId,
                type: 'category'
            }];
            
            // Determine which team ID to use based on user role and selection
            let shareTeamId = '';
            if (shareType === 'team') {
                if (canShareWithTeam) {
                    shareTeamId = selectedTeamId;
                } else {
                    // For regular users, use their department ID
                    shareTeamId = team_id || '';
                }
            }

            const response = await ShareService.shareItems({
                itemIds,
                sharedWithEmail: email,
                shareType,
                permissionLevel,
                expiresAt,
                sharedKey: '',
                teamId: shareTeamId,
                workspaceId: activeWorkspace?.id || team_id || ''
            });

            if (response && response[0] && response[0].success) {
                toast.success('Vault shared successfully');
                if (onSuccess) {
                    await onSuccess();
                }
                onClose();
            } else {
                toast.error(response?.message || 'Failed to share vault');
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to share vault';
            toast.error(errorMessage);
            console.error('Share error:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Add function to fetch roles
    const fetchRoles = async () => {
        try {
            const response = await RolesService.getRoles(team_id || undefined);
            setRoles(response.filter(role => role.id !== 1) || []);
            
        } catch (error) {
            console.error('Error fetching roles:', error);
            setInviteError('Failed to load roles');
        }
    };

    // Add function to validate email
    const validateEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    // Add function to handle invitation
    const handleInviteUser = async () => {
        // Validation
        if (!validateEmail(searchText)) {
            setInviteError('Please enter a valid email address');
            return;
        }

        if (!inviteRoleId) {
            setInviteError('Please select a role');
            return;
        }

        try {
            setIsInviting(true);
            setInviteError('');

            await TeamService.inviteUser(searchText, inviteRoleId, team_id || '', team_id || '');
            
            // Add the invited user to the filteredUsers list so they appear as selected
            const newUser = { id: 'invited-' + Date.now(), email: searchText };
            setSelectedUser(newUser);
            setSelectedUserId(newUser.id);
            setEmail(searchText);
            
            setTimeout(() => {  
                toast.success(`Invitation sent. Please wait for them to accept the invitation.`);
            }, 2000);
            setShowInviteForm(false);
        } catch (error: any) {
            console.error('Error inviting user:', error);
            setInviteError(error.message || 'Failed to send invitation');
        } finally {
            setIsInviting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-hidden">
            <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"></div>

            <div className="fixed inset-0 flex items-center justify-center p-4">
                <div className="relative bg-white rounded-lg w-full max-w-md max-h-[90vh] flex flex-col shadow-lg overflow-hidden">
                    <div className="flex items-center justify-between p-4 border-b">
                        <h3 className="text-lg font-medium">
                            Share Vault: {vaultName}
                        </h3>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-500"
                        >
                            <XMarkIcon className="h-6 w-6" />
                        </button>
                    </div>
                    <form onSubmit={handleShare} className="flex-1 overflow-auto p-4">
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Share with
                                </label>
                                <div className="flex space-x-4">
                                    <label className="inline-flex items-center">
                                        <input
                                            type="radio"
                                            className="form-radio"
                                            value="individual"
                                            checked={shareType === 'individual'}
                                            onChange={() => setShareType('individual')}
                                        />
                                        <span className="ml-2">Individual</span>
                                    </label>
                                    {team_id && canShareWithTeam && (
                                    <label className="inline-flex items-center">
                                        <input
                                            type="radio"
                                            className="form-radio"
                                            value="team"
                                            checked={shareType === 'team'}
                                            onChange={() => setShareType('team')}
                                        />
                                        <span className="ml-2">Team</span>
                                    </label>
                                    )}
                                </div>
                            </div>

                            {shareType === 'individual' && (
                                <div className="relative" ref={dropdownRef}>
                                    <label htmlFor="user-search" className="block text-sm font-medium text-gray-700">
                                        Search/Select User
                                    </label>
                                    <input
                                        id="user-search"
                                        type="text"
                                        value={searchText}
                                        onChange={handleSearchChange}
                                        onFocus={handleFocus}
                                        placeholder="Search by email"
                                        className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                        autoComplete="off"
                                    />
                                    
                                    {!selectedUserId && isDropdownOpen && filteredUsers.length > 0 && (
                                        <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md border border-gray-300 max-h-60 overflow-auto">
                                            <ul className="py-1">
                                                {filteredUsers.map(user => (
                                                    <li 
                                                        key={user.id} 
                                                        className={`px-3 py-2 hover:bg-blue-50 cursor-pointer`}
                                                        onClick={() => handleUserSelect(user)}
                                                    >
                                                        {user.email}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    
                                    {isDropdownOpen && searchText.length > 0 && filteredUsers.length === 0 && (
                                        <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md border border-gray-300 p-3">
                                            {!showInviteForm ? (
                                                <div className="text-center">
                                                    <p className="text-gray-500 mb-2">No matching users found</p>
                                                    {validateEmail(searchText) && (
                                                        <button
                                                            type="button"
                                                            onClick={() => setShowInviteForm(true)}
                                                            className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                                                        >
                                                            Invite {searchText} to join
                                                        </button>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="space-y-3">
                                                    <h4 className="text-sm font-medium text-gray-700">Invite {searchText}</h4>
                                                    
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-500">
                                                            Role
                                                        </label>
                                                        <select
                                                            value={inviteRoleId}
                                                            onChange={(e) => setInviteRoleId(Number(e.target.value))}
                                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                                                        >
                                                            <option value={0}>Select Role</option>
                                                            {roles.map((role) => (
                                                                <option key={role.id} value={role.id}>
                                                                    {role.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    
                                                    {inviteError && (
                                                        <p className="text-xs text-red-600">{inviteError}</p>
                                                    )}
                                                    
                                                    <div className="flex justify-end space-x-2">
                                                        <button
                                                            type="button"
                                                            onClick={() => setShowInviteForm(false)}
                                                            className="text-gray-700 hover:text-gray-900 text-xs px-2 py-1"
                                                        >
                                                            Cancel
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={handleInviteUser}
                                                            disabled={isInviting || !inviteRoleId}
                                                            className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-1 rounded-md disabled:opacity-50"
                                                        >
                                                            {isInviting ? 'Sending...' : 'Send Invitation'}
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                            
                            {shareType === 'team' && canShareWithTeam && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        Team
                                    </label>
                                    <select
                                        value={selectedTeamId}
                                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedTeamId(e.target.value)}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                        required
                                    >
                                        <option value="">Select Team</option>
                                        {organizations.map((org: Team) => (
                                            <option key={org.id} value={org.id}>
                                                {org.name}
                                            </option>
                                        ))}
                                        <option value={`${team_id}`}>Share With All Members</option>
                                    </select>
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    Permission Level
                                </label>
                                <select
                                    value={permissionLevel}
                                    onChange={(e) => setPermissionLevel(e.target.value as 'read' | 'write' | 'manage')}
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                >
                                    <option value="read">View Only</option>
                                    <option value="write">View & Edit</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    Expires In
                                </label>
                                <select
                                    value={expiresIn}
                                    onChange={(e) => setExpiresIn(e.target.value)}
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                >
                                    <option value="never">Never</option>
                                    <option value="1">24 hours</option>
                                    <option value="7">7 days</option>
                                    <option value="30">30 days</option>
                                    <option value="90">90 days</option>
                                </select>
                            </div>
                        </div>

                        <div className="flex justify-end space-x-3 mt-6">
                            <Button type="button" variant="secondary" onClick={onClose}>
                                Cancel
                            </Button>
                            <Button type="submit" loading={isLoading}>
                                Share Vault
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ShareVaultModal; 